import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ThumbsUp, ThumbsDown, Share2, Bookmark } from 'lucide-react';
import './CareerNewsCarousel.css';

const CareerNewsCarousel = ({ resumeData, interests = [] }) => {
  const [newsArticles, setNewsArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

  useEffect(() => {
    fetchCareerNews();
  }, [resumeData, interests]);

  const fetchCareerNews = async () => {
    setLoading(true);
    try {
      // Extract industries from resume data
      const industries = resumeData?.industries || interests || [];
      
      // Temporarily use mock data OR implement endpoint later
      // The backend endpoint /api/fetch-career-news returns 404, so we disable it for now.
      
      // const response = await fetch(`${BACKEND_URL}/api/fetch-career-news`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ industries })
      // });

      // if (!response.ok) {
        // throw new Error(`Failed to fetch news: ${response.status}`);
      // }

      // const data = await response.clone().json();
      // setNewsArticles(data.articles || []);
      
      setNewsArticles([]); // Start with empty so we don't spam 404s
    } catch (error) {
      console.error('Error fetching career news:', error);
      // Fallback to empty array
      setNewsArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % Math.max(1, Math.ceil(newsArticles.length / 3)));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + Math.ceil(newsArticles.length / 3)) % Math.ceil(newsArticles.length / 3));
  };

  const getVisibleArticles = () => {
    const start = currentIndex * 3;
    return newsArticles.slice(start, start + 3);
  };

  if (loading) {
    return (
      <div className="career-news-carousel-section">
        <div className="news-header">
          <h2 className="news-title">Career & Industry News</h2>
          <p className="news-subtitle">Stay updated with the latest in your field</p>
        </div>
        <div className="news-loading">
          <p>Loading news...</p>
        </div>
      </div>
    );
  }

  if (newsArticles.length === 0) {
    return null;
  }

  const visibleArticles = getVisibleArticles();
  const totalSlides = Math.ceil(newsArticles.length / 3);

  return (
    <div className="career-news-carousel-section">
      <div className="news-header">
        <h2 className="news-title">Career & Industry News</h2>
        <p className="news-subtitle">Stay updated with the latest in your field</p>
      </div>

      <div className="carousel-container">
        <button
          className="carousel-button carousel-button-prev"
          onClick={prevSlide}
          disabled={totalSlides <= 1}
          aria-label="Previous"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>

        <div className="carousel-track">
          <div
            className="carousel-slide"
            style={{
              transform: `translateX(-${currentIndex * 100}%)`
            }}
          >
            {visibleArticles.map((article, index) => (
              <div key={index} className="news-card">
                <div className="news-thumbnail">
                  <div className="news-thumbnail-icon" style={{ backgroundColor: getThumbnailColor(index) }}>
                    {getThumbnailIcon(index)}
                  </div>
                </div>
                <div className="news-content">
                  <h3 className="news-card-title">{article.title}</h3>
                  <p className="news-card-description">{article.description}</p>
                  <div className="news-card-meta">
                    <span className="news-source">{article.source || 'Career Insights'}</span>
                    <span className="news-date">{article.date || 'Recent'}</span>
                  </div>
                </div>
                <div className="news-card-actions">
                  <button className="news-action-btn" aria-label="Like">
                    <ThumbsUp className="news-action-icon" />
                  </button>
                  <button className="news-action-btn" aria-label="Dislike">
                    <ThumbsDown className="news-action-icon" />
                  </button>
                  <button className="news-action-btn" aria-label="Share">
                    <Share2 className="news-action-icon" />
                  </button>
                  <button className="news-action-btn" aria-label="Save">
                    <Bookmark className="news-action-icon" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          className="carousel-button carousel-button-next"
          onClick={nextSlide}
          disabled={totalSlides <= 1}
          aria-label="Next"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {totalSlides > 1 && (
        <div className="carousel-dots">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Helper functions for thumbnails
const getThumbnailColor = (index) => {
  const colors = ['#87CEEB', '#9333EA', '#4ECDC4', '#FF6B6B', '#FFD93D', '#86a17d'];
  return colors[index % colors.length];
};

const getThumbnailIcon = (index) => {
  const icons = ['signal', 'code', 'chart', 'trend'];
  const iconType = icons[index % icons.length];
  
  if (iconType === 'signal') {
    return (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="20" r="3" fill="white" />
        <path d="M20 20 L20 10" stroke="white" strokeWidth="2" />
        <path d="M20 20 L20 30" stroke="white" strokeWidth="2" />
        <path d="M20 20 L10 20" stroke="white" strokeWidth="2" />
        <path d="M20 20 L30 20" stroke="white" strokeWidth="2" />
      </svg>
    );
  } else if (iconType === 'code') {
    return (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <path d="M12 20 L8 16 L12 12" stroke="white" strokeWidth="2" fill="none" />
        <path d="M28 20 L32 16 L28 12" stroke="white" strokeWidth="2" fill="none" />
        <path d="M18 28 L22 12" stroke="white" strokeWidth="2" />
      </svg>
    );
  } else if (iconType === 'chart') {
    return (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <rect x="8" y="24" width="6" height="8" fill="white" />
        <rect x="16" y="18" width="6" height="14" fill="white" />
        <rect x="24" y="12" width="6" height="20" fill="white" />
      </svg>
    );
  } else {
    return (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="15" r="5" fill="white" />
        <path d="M20 10 L25 25 L30 20" stroke="white" strokeWidth="2" fill="none" />
        <path d="M20 10 L15 25 L10 20" stroke="white" strokeWidth="2" fill="none" />
      </svg>
    );
  }
};

export default CareerNewsCarousel;

